import { InfiniteDataSource } from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import { useEffect } from 'react';

export type DrainedDataSource<TData, TFilter> = Omit<
    InfiniteDataSource<TData, TFilter>,
    'hasNext' | 'loadNext' | 'paging'
>;

export type UseDrainDataSourceOptions = Readonly<{
    /**
     * The drain mode (defaults to `'end'`):
     *
     * * `'page'` - returns updated results after each page is loaded.
     * * `'end'` - returns results after all pages have loaded.
     */
    mode?: 'page' | 'end';
}>;

/**
 * Creates a data source that will load all results from
 * the supplied data source.
 *
 * @param param0 The data source to drain.
 * @param options Additional options
 */
export function useDrainDataSource<TData, TFilter>(
    {
        results: dataResults,
        hasNext,
        loadNext,
        isLoading: isDataLoading,
        ...rest
    }: InfiniteDataSource<TData, TFilter>,
    options?: UseDrainDataSourceOptions
): DrainedDataSource<TData, TFilter> {
    // Trigger loading next page if more data is available.
    useEffect(() => {
        if (hasNext && !isDataLoading) {
            loadNext();
        }
    });

    const mode = options?.mode ?? 'end';
    const isLoading = isDataLoading || (hasNext ?? false);

    // Only return results after all pages have been loaded if mode is 'end'.
    const results = isLoading && mode === 'end' ? undefined : dataResults;

    return {
        isLoading,
        results,
        ...rest,
    };
}
